import { MapUtils } from '../../../utility/map-utils.js';
import { generateUniqueDatabaseId, isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { EventTicketOrder } from '../event-ticket-order.js';
import { TimeZone } from '../../../time/timezone.js';
import { Dayjs } from 'dayjs';
import { TrueTime } from '../../../time/true-time.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

const TAX_REQUEST_ID_LENGTH: number = 28;

export enum TaxIdentificationRequestStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED',
  Expired = 'EXPIRED',
}

export class TaxIdentificationRequest extends DataModel {
  static readonly TYPE_ID: string = 'f5d92e82571a4af4908bc2f0db57057f';

  constructor(
    public readonly uniqueId: string,
    public readonly creationDate: Dayjs,
    public readonly expirationDate: Dayjs,
    public readonly orderId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly status: TaxIdentificationRequestStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return TaxIdentificationRequest.TYPE_ID;
  }

  static fromOrder(order: EventTicketOrder, expirationDate: Dayjs): TaxIdentificationRequest {
    return new TaxIdentificationRequest(
        generateUniqueDatabaseId({ length: TAX_REQUEST_ID_LENGTH }),
        TrueTime.now(),
        expirationDate,
        order.uniqueId,
        order.businessRef.clone(),
        TaxIdentificationRequestStatus.Pending,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): TaxIdentificationRequest {
    return new TaxIdentificationRequest(
        MapUtils.get<string>(map, 'uniqueId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDate(MapUtils.get<number>(map, 'expirationDate')),
        MapUtils.get<string>(map, 'orderId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), TaxIdentificationRequestStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      'expirationDate': context.encodeDate(this.expirationDate),
      'orderId': this.orderId,
      'businessRef': this.businessRef.encode(),
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new TaxIdentificationRequest(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.expirationDate),
        this.orderId,
        this.businessRef.clone(),
        this.status,
    ) as this;
  }

  isSame(other: TaxIdentificationRequest): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId == this.uniqueId &&
      other.creationDate === this.creationDate &&
      other.expirationDate === this.expirationDate &&
      other.orderId == this.orderId &&
      other.status === this.status &&
      isSameData(other.businessRef, this.businessRef);
  }

  copyWith(data: Partial<TaxIdentificationRequest>): this {
    return new TaxIdentificationRequest(
        data.uniqueId ?? this.uniqueId,
        data.creationDate ?? TimeZone.cloneDate(this.creationDate),
        data.expirationDate ?? TimeZone.cloneDate(this.expirationDate),
        data.orderId ?? this.orderId,
        data.businessRef ?? this.businessRef.clone(),
        data.status ?? this.status,
    ) as this;
  }

  static encodedStatusSafe(status: TaxIdentificationRequestStatus): string {
    return fromEnumAsString(status);
  }
}

export class TaxIdentificationRequestBuilder extends DataModelBuilder<TaxIdentificationRequest> {
  build(context: SerializationContext, map: SerializedData): TaxIdentificationRequest {
    return TaxIdentificationRequest.fromMap(context, map);
  }
}
